import { useCallback, useEffect, useRef, useState } from "react";
import { api, type AdminArtifactView, type AdminSortKey, type Visibility } from "../lib/api";
import { useUser } from "../lib/auth";
import { AllArtifactsTable } from "../components/admin/AllArtifactsTable";
import { DEFAULT_SORT, nextSort, type SortState } from "../components/admin/sortState";

type StaleFilter = "" | "30" | "60" | "90";

const PAGE_SIZE = 50;

/** Admin governance view of every artifact org-wide. */
export function AllArtifactsPage() {
  const { user } = useUser();
  const [items, setItems] = useState<AdminArtifactView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [vis, setVis] = useState<"" | Visibility>("");
  const [statusFilter, setStatusFilter] = useState<"active" | "deleted" | "all">("active");
  const [staleFilter, setStaleFilter] = useState<StaleFilter>("");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  const { key: sortKey, dir: sortDir } = sort;
  const load = useCallback(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    setLoading(true);
    api.admin.artifacts
      .list({
        q: q || undefined,
        visibility: vis || undefined,
        status: statusFilter,
        stale_days: staleFilter ? Number(staleFilter) : undefined,
        sort: sortKey,
        direction: sortDir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      })
      .then((data) => { if (!cancelled) { setItems(data); setLoading(false); } })
      .catch((e: Error) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [q, vis, statusFilter, staleFilter, sortKey, sortDir, page]);
  useEffect(load, [load]);

  if (user?.role !== "admin") {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-rose-900">
        <h2 className="text-base font-semibold">Forbidden</h2>
        <p className="mt-1 text-sm">You need the admin role to view this page.</p>
      </div>
    );
  }

  async function changeVisibility(slug: string, next: Visibility) {
    setBusy(`vis:${slug}`);
    try {
      await api.admin.artifacts.setVisibility(slug, next);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function togglePermanent(slug: string, current: boolean) {
    setBusy(`perm:${slug}`);
    try {
      await api.admin.artifacts.setPermanent(slug, !current);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function restore(slug: string) {
    setBusy(`restore:${slug}`);
    try {
      await api.admin.artifacts.restore(slug);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function forceDelete(slug: string, title: string) {
    if (
      !confirm(
        `Hard-delete "${title}" (${slug})? This bypasses the 30-day grace ` +
          `window — GCS objects and Firestore doc are removed immediately.`,
      )
    ) {
      return;
    }
    setBusy(`del:${slug}`);
    try {
      await api.admin.artifacts.forceDelete(slug);
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Apply a header click: toggle/switch the sort and return to the first page. */
  function handleSort(key: AdminSortKey) {
    setSort((s) => nextSort(s, key));
    setPage(0);
  }

  const hasMore = items !== null && items.length === PAGE_SIZE;

  function goTo(next: number) {
    setPage(next);
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div ref={topRef}>
      <header className="mb-2 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">All artifacts</h1>
      </header>
      <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <strong>Governance view.</strong> Every artifact org-wide, including
        private and soft-deleted. Admin actions are audit-logged.{" "}
        <strong>Private</strong> artifacts are owner-only — you can manage their
        lifecycle here, but their content can&rsquo;t be opened and their
        visibility can&rsquo;t be changed.
      </p>

      <section className="mb-4 flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-3">
        <input
          type="search"
          placeholder="Search title, description, slug, owner…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          className="min-w-[260px] flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:ring-slate-500"
        />
        <select
          value={vis}
          onChange={(e) => { setVis(e.target.value as "" | Visibility); setPage(0); }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="">Visibility: any</option>
          <option value="private">Private</option>
          <option value="unlisted">Unlisted</option>
          <option value="shared">Shared</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as "active" | "deleted" | "all"); setPage(0); }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="active">Active</option>
          <option value="deleted">Soft-deleted</option>
          <option value="all">All</option>
        </select>
        <select
          value={staleFilter}
          onChange={(e) => { setStaleFilter(e.target.value as StaleFilter); setPage(0); }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          title="Filter to artifacts not viewed recently (or never viewed)"
        >
          <option value="">Last viewed: any</option>
          <option value="30">Stale &gt; 30d</option>
          <option value="60">Stale &gt; 60d</option>
          <option value="90">Stale &gt; 90d</option>
        </select>
      </section>

      {error && (
        <p className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : items !== null && items.length === 0 ? (
        <p className="text-slate-500">No artifacts match these filters.</p>
      ) : items !== null ? (
        <AllArtifactsTable
          items={items}
          sort={sort}
          onSort={handleSort}
          busy={busy}
          currentUserEmail={user?.email}
          onChangeVisibility={changeVisibility}
          onTogglePermanent={togglePermanent}
          onRestore={restore}
          onForceDelete={forceDelete}
        />
      ) : null}

      {(page > 0 || hasMore) && (
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-slate-500">Page {page + 1}</span>
          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={!hasMore}
            className="rounded-md border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
